import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useCustomers } from "@/customers/useCustomers";
import { MultiSelect, type Option } from "@/components/ui/multi-select";
import { CodingDashboardDatePickers } from "@/admin-coding-dashboard/CodingDashboardDatePickers";
import { parseEncounterIds } from "@/pages/admin/parseEncounterIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useEncounterById, type RawFilesViewerEncounter } from "./useEncounterById";
import { useFilteredEncounters, hasActiveEncounterFilter, type EncounterFilters } from "./useFilteredEncounters";
import { parsePatientId, readEncounterFilterParams, writeEncounterFilterParams } from "./encounterFilterParams";

interface EncounterPickerProps {
  onSelectEncounter: (encounter: RawFilesViewerEncounter) => void;
}

export function EncounterPicker({ onSelectEncounter }: EncounterPickerProps) {
  const { customers, isLoading: isLoadingCustomers } = useCustomers();
  const customerOptions: Option[] = customers.map((c) => ({ value: c.id, label: c.label }));

  const [directIdInput, setDirectIdInput] = useState("");
  const [directId, setDirectId] = useState<string | null>(null);
  const {
    data: directEncounter,
    isLoading: isLoadingDirectEncounter,
    error: directEncounterError,
  } = useEncounterById(directId);
  const directLookupCompletedWithNoResult =
    directId !== null && !isLoadingDirectEncounter && !directEncounterError && directEncounter === null;

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readEncounterFilterParams(searchParams);
  const selectedCustomers: Option[] = filters.customerIds.map(
    (id) => customerOptions.find((option) => option.value === id) ?? { value: id, label: id },
  );

  // Replace rather than push: the back button should land on the list as the reviewer left it,
  // not walk back through one history entry per filter tweak.
  const updateFilters = (change: Partial<EncounterFilters>) =>
    setSearchParams(
      (current) => writeEncounterFilterParams(current, { ...readEncounterFilterParams(current), ...change }),
      { replace: true },
    );

  const [patientIdInput, setPatientIdInput] = useState(filters.patientId ?? "");
  const [patientIdIsInvalid, setPatientIdIsInvalid] = useState(false);

  const isFilterActive = hasActiveEncounterFilter(filters);
  const {
    data: filteredEncounters,
    isLoading: isLoadingEncounters,
    error: filteredEncountersError,
  } = useFilteredEncounters(filters);

  const handleGo = () => {
    const [id] = parseEncounterIds(directIdInput);
    setDirectId(id ?? null);
  };

  const handlePatientIdGo = () => {
    const typed = patientIdInput.trim();
    const patientId = parsePatientId(typed);
    setPatientIdIsInvalid(typed !== "" && patientId === null);
    updateFilters({ patientId });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fires only on the fetch-success transition; onSelectEncounter isn't memoized by callers, so including it would refire this effect on unrelated re-renders
  useEffect(() => {
    if (directId && directEncounter) {
      onSelectEncounter(directEncounter);
    }
  }, [directId, directEncounter]);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="encounter-id">Encounter id</Label>
        <div className="flex gap-2">
          <Input
            id="encounter-id"
            value={directIdInput}
            onChange={(e) => setDirectIdInput(e.target.value)}
            placeholder="Paste an encounter id"
          />
          <Button onClick={handleGo}>Go</Button>
        </div>
        {directEncounterError && <div className="text-destructive">Error loading encounters. Please try again.</div>}
        {directLookupCompletedWithNoResult && <div>No encounter found for that id.</div>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="patient-id">Patient id</Label>
        <div className="flex gap-2">
          <Input
            id="patient-id"
            value={patientIdInput}
            onChange={(e) => setPatientIdInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePatientIdGo();
            }}
            placeholder="Paste a patient id"
          />
          <Button onClick={handlePatientIdGo}>Go</Button>
        </div>
        {patientIdIsInvalid && <div className="text-destructive">That is not a valid patient id.</div>}
      </div>

      <div className="space-y-2">
        <Label>Filter by customer and discharge date</Label>
        <div className="flex gap-4 items-start">
          <MultiSelect
            options={customerOptions}
            value={selectedCustomers}
            onChange={(options) => updateFilters({ customerIds: options.map((option) => option.value) })}
            placeholder="Select customers..."
            isLoading={isLoadingCustomers}
          />
          <CodingDashboardDatePickers
            fromDate={filters.dischargeFrom}
            toDate={filters.dischargeTo}
            onFromDateChange={(date) => updateFilters({ dischargeFrom: date })}
            onToDateChange={(date) => updateFilters({ dischargeTo: date })}
          />
        </div>
      </div>

      {isLoadingEncounters && <div className="text-sm text-muted-foreground">Loading encounters…</div>}

      {filteredEncountersError && <div className="text-destructive">Error loading encounters. Please try again.</div>}

      {isFilterActive && !isLoadingEncounters && !filteredEncountersError && filteredEncounters?.length === 0 && (
        <div>No encounters match these filters.</div>
      )}

      {filteredEncounters && filteredEncounters.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Patient ID</TableHead>
              <TableHead>Encounter ID</TableHead>
              <TableHead>Admit</TableHead>
              <TableHead>Discharge</TableHead>
              <TableHead>Specialty</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEncounters.map((encounter) => (
              <TableRow
                key={encounter.id}
                className="cursor-pointer hover:bg-muted"
                onClick={() => onSelectEncounter(encounter)}
              >
                <TableCell className="font-mono text-xs">{encounter.patient_id}</TableCell>
                <TableCell className="font-mono text-xs">{encounter.id}</TableCell>
                <TableCell>{encounter.admit_datetime ?? "—"}</TableCell>
                <TableCell>{encounter.discharge_datetime ?? "—"}</TableCell>
                <TableCell>{encounter.specialty}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
